import { PlatformConfig, AgentProfile } from "../types";

// Backend API URL
const BACKEND_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:3001';

/**
 * Chat Service
 * Handles all AI chat communication via secure backend API
 * Supports multiple AI providers (Claude, OpenAI, Grok, Gemini)
 */
export class ChatService {
  private config: PlatformConfig;
  private currentAgent: AgentProfile;
  private history: Array<{role: string, text: string}> = [];

  constructor(config: PlatformConfig, agent: AgentProfile) {
    this.config = config;
    this.currentAgent = agent;
  }

  /**
   * Update the configuration and agent
   */
  public updateConfig(newConfig: PlatformConfig, newAgent: AgentProfile) {
    this.config = newConfig;
    this.currentAgent = newAgent;
  }

  /**
   * Send a message and receive a streaming response
   * All AI processing happens on the backend with secure API key management
   */
  public async sendMessageStream(message: string): Promise<AsyncIterable<string>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          siteId: this.config.id,
          message: message,
          agentId: this.currentAgent.id,
          history: this.history
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `Backend error: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body from backend");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Track history for context
      this.history.push({ role: 'user', text: message });

      let fullResponse = '';
      const chatService = this; // Capture reference to ChatService instance

      return {
        [Symbol.asyncIterator]: async function* () {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              fullResponse += chunk;
              yield chunk;
            }
          } finally {
            // Save complete AI response to history for conversation continuity
            if (fullResponse.trim()) {
              chatService.history.push({
                role: 'model',
                text: fullResponse.trim()
              });
              console.log('[ChatService] Response saved to history. Total messages:', chatService.history.length);
            }
          }
        }
      };

    } catch (error) {
      console.error('Chat service error:', error);
      throw error;
    }
  }

  /**
   * Get the current AI provider being used
   */
  public getProvider(): string {
    return this.config.aiProvider || 'gemini';
  }

  /**
   * Clear conversation history
   */
  public clearHistory(): void {
    this.history = [];
  }
}